"use client"

import * as React from "react"

import { NAV_FLYOUT_SCROLL_BODY } from "@exxatdesignux/ui/lib/nav-flyout-inset"
import { cn } from "@/lib/utils"

const PANEL_TITLE_CLASS =
  "m-0 min-w-0 truncate py-0.5 text-base font-semibold tracking-tight text-sidebar-foreground"

/**
 * Shared column panel for `NewFocusTemplate variant="form-inspector"` — fixed
 * chrome with a scrollable body. Used by the builder / work column and the
 * inspector rail.
 *
 * `surface="secondary-panel"` uses the same elevation as library / Ask Leo
 * (`--secondary-panel-bg`), not the page canvas card.
 */
export function ComposerPanelCard({
  title,
  headerActions,
  footer,
  bodyClassName,
  className,
  surface = "card",
  /** Stretch to fill the column (compose shells). Off when content should hug. */
  fillHeight = true,
  /** Page scroll — parent is viewport-tall sticky rail; card fills height and body scrolls. */
  pageScrollBody = false,
  children,
}: {
  title?: string
  headerActions?: React.ReactNode
  /** Sticky below the scroll body (e.g. inspector Question Ready). */
  footer?: React.ReactNode
  bodyClassName?: string
  className?: string
  surface?: "card" | "secondary-panel"
  fillHeight?: boolean
  pageScrollBody?: boolean
  children: React.ReactNode
}) {
  const isSecondaryPanel = surface === "secondary-panel"

  return (
    <div
      className={cn(
        "flex w-full min-w-0 flex-col overflow-hidden",
        isSecondaryPanel
          ? "bg-[var(--secondary-panel-bg)]"
          : "rounded-xl border border-border bg-card",
        pageScrollBody || fillHeight ? "min-h-0 flex-1" : "",
        !pageScrollBody && "max-h-full min-h-0",
        className,
      )}
    >
      {title || headerActions ? (
        <div
          className={cn(
            "flex shrink-0 items-center justify-between gap-2",
            !title && headerActions && "justify-end",
            isSecondaryPanel
              ? "h-(--header-height) border-b border-sidebar-border/50 px-3"
              : "border-b border-border px-4 py-3 sm:px-6",
          )}
        >
          {title ? (
            <h2
              className={
                isSecondaryPanel
                  ? PANEL_TITLE_CLASS
                  : "text-sm font-semibold text-foreground"
              }
            >
              {title}
            </h2>
          ) : null}
          {headerActions}
        </div>
      ) : null}
      <div
        className={cn(
          NAV_FLYOUT_SCROLL_BODY,
          isSecondaryPanel ? "px-3 py-3" : "px-4 py-3 pb-3 sm:px-5 sm:py-3 sm:pb-3",
          pageScrollBody || fillHeight ? "flex-1" : "",
        )}
      >
        <div className={cn("flex flex-col", bodyClassName ?? "gap-4")}>
          {children}
        </div>
      </div>
      {footer ? (
        <div
          className={cn(
            "shrink-0 border-t px-4 py-3 sm:px-5",
            isSecondaryPanel ? "border-sidebar-border/50" : "border-border",
          )}
        >
          {footer}
        </div>
      ) : null}
    </div>
  )
}
